import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFCompletedDK from '../components/RMFCompleted/components/RMFCompletedDK/RMFCompletedDK';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFIdentityVerification from '../components/RMFIdentityVerification/RMFIdentityVerification';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFValidationError from '../components/RMFValidationError/RMFValidationError';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfDKFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const {
    invokeLegalNameSave,
    invokeSignupDob,
    forkDobCheck,
    jumpToFeatureStep,
    forkSignupDepositCheckDone
  } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const forkCheckDKErrors = new Fork(
    Guard.ShouldShowValidationScreenDK,
    RMFValidationError,
    RMFDepositLimit
  );
  const forkStandaloneRMFCheck = new Fork(Guard.HideRMFCompletion, [forkRmfExit], RMFCompletedDK);
  const invokeEnableRealMoneyFlag = new Invoke(
    Service.RmfEnableRealMoneyFlagDK,
    forkStandaloneRMFCheck,
    forkCheckDKErrors
  );

  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFIdentityVerification, RMFAddress);

  const invokeIdentityVerificationSave = new Invoke(
    Service.RmfIdentifyVerificationSaveDK,
    RMFDepositLimit,
    RMFIdentityVerification
  );

  const checkMitIdContextState = new Fork(
    Guard.MitIdDataPresent,
    invokeEnableRealMoneyFlag,
    invokeSignupDob
  );

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    invokeEnableRealMoneyFlag,
    RMFDepositLimit
  );

  const invokeMitIdContextState = new Invoke(
    Service.RmfMitIdData,
    checkMitIdContextState,
    invokeSignupDob
  );

  const checkMitIdStandaloneState = new Fork(
    Guard.StandaloneRMFMitIdDataPresent,
    [invokeMitIdContextState],
    [checkMitIdContextState]
  );

  return [
    checkMitIdStandaloneState,
    invokeSignupDob,
    forkDobCheck,
    invokeLegalNameSave,
    RMFAddress,
    invokeAddressSave,
    RMFIdentityVerification,
    invokeIdentityVerificationSave,
    RMFDepositLimit,
    invokeDepositLimitSave,
    invokeEnableRealMoneyFlag,
    forkCheckDKErrors,
    RMFValidationError,
    forkStandaloneRMFCheck,
    RMFCompletedDK,
    forkRmfExit,
    jumpToFeatureStep
  ];
};
export default useRmfDKFlow;
